import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { useSuppressMapBackground } from '../contexts/MapBackgroundContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  Upload, 
  FileText, 
  Image as ImageIcon,
  Trash2,
  Download,
  Pencil,
  Building2,
  Layers,
  Home,
  Plus,
  Settings,
  FileSpreadsheet,
  Check,
  X,
  Clock,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  FileDown,
  Settings2,
  FolderOpen,
  Boxes,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import HUDPageHeader from '../components/HUDPageHeader';
import MaterialthequePendingBanner from '../components/materialtheque/MaterialthequePendingBanner';
import GeolocationBanner from '../components/project/GeolocationBanner';
import MaterialthequeStatusCard from '../components/materialtheque/MaterialthequeStatusCard';
// V14.68.22 archived: ProjectSubSidebar replaced by ProjectHorizontalNav
import ProjectHorizontalNav from '../components/project/ProjectHorizontalNav';
import ProjectDashboardSection from '../components/project/ProjectDashboardSection';
import ProductionSection from '../components/project/ProductionSection';
import ProjectResourcesSection from '../components/project/ProjectResourcesSection';
import ProjectSettingsHub from '../components/project/ProjectSettingsHub';
import { resolveSection } from '../components/project/projectSections';
import LotCorrespondanceField from '../components/project/LotCorrespondanceField';
import LotInheritanceBanner from '../components/project/LotInheritanceBanner';
import CorrespondanceBadge from '../components/project/CorrespondanceBadge';
import ProjectFilesWorkspace from '../components/project/ProjectFilesWorkspace';
import LotKpiBar, { LotStatusPill, FloorWorkflowBar, computeLotStatus } from '../components/project/LotKpiBar';
import LotThumbnail from '../components/project/LotThumbnail';
import SalesBrochureDialog from '../components/project/SalesBrochureDialog';
import { getProjectVocab } from '../lib/projectVocab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPOLOGIES = ['Studio', 'T1', 'T2', 'T3', 'T4', 'T5', 'Duplex', 'Maison', 'Bureau', 'Commerce'];

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  // V14.68.6 — Sync du fond map global sur les coords du projet actif.
  useSuppressMapBackground(); // V15.54 — pages projet : fond uni sombre, sans carte (interface de production)
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState({});
  const [lots, setLots] = useState({});
  const [files, setFiles] = useState([]);
  const [allLots, setAllLots] = useState([]);
  const [styles, setStyles] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // V17.0 — 4 sections (dashboard · production · ressources · settings) + sous-onglet ; anciennes URL résolues par resolveSection.
  const readUrl = () => { try { const q = new URLSearchParams(window.location.search); return resolveSection(q.get('section'), q.get('tab'), q); } catch { return { section: 'dashboard', tab: null }; } };
  const [nav, setNav] = useState(readUrl);
  const activeSection = nav.section;
  const activeTab = nav.tab;
  const [focus, setFocus] = useState(null);  // { lotId, buildingId } posé par le Dashboard / arbre
  const openSection = useCallback((section, tab = null, extra = null) => {
    const r = resolveSection(section, tab, null);
    const url = new URL(window.location.href);
    url.searchParams.delete('lot_id'); url.searchParams.delete('building_id'); url.searchParams.delete('review');
    if (extra?.lotId) url.searchParams.set('lot_id', extra.lotId);
    else if (extra?.buildingId) url.searchParams.set('building_id', extra.buildingId);
    window.history.replaceState({}, '', url.toString());
    setFocus(extra);
    setNav(r);
  }, []);
  const setActiveSection = (s) => openSection(s);
  // navigation interne (?section=…&tab=…) : resynchronise depuis l'URL.
  const location = useLocation();
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (!q.get('section')) return;
    const r = resolveSection(q.get('section'), q.get('tab'), q);
    setNav((cur) => (cur.section === r.section && cur.tab === r.tab ? cur : r));
  }, [location.key, location.search]);
  const [engTab, setEngTab] = useState('structure');
  const [pendingForBadge, setPendingForBadge] = useState(false);
  // Engineering UX state
  const [engBuildingFilter, setEngBuildingFilter] = useState('all'); // 'all' | building_id
  const [expandedLotsByFloor, setExpandedLotsByFloor] = useState({}); // { [floor_id]: true/false }
  const LOT_PREVIEW_LIMIT = 20;
  // Hierarchical collapsibles (Iter 65) — building & floor & individual lot rows
  const [collapsedBuildings, setCollapsedBuildings] = useState(() => new Set());
  const [collapsedFloors, setCollapsedFloors] = useState(() => new Set());
  const [expandedLotRow, setExpandedLotRow] = useState(null); // lot_id of the currently expanded row
  const toggleBuildingCollapse = (id) => setCollapsedBuildings((p) => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleFloorCollapse = (id) => setCollapsedFloors((p) => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const [genByLot, setGenByLot] = useState({}); // { [lot_id]: 'completed' }
  const [showSalesBrochure, setShowSalesBrochure] = useState(false);
  const [generations, setGenerations] = useState([]);
  const [loadingGenerations, setLoadingGenerations] = useState(false);
  const fetchGenerations = useCallback(async () => {
    if (!projectId) return;
    setLoadingGenerations(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/generations`, { withCredentials: true });
      const items = Array.isArray(r.data?.items) ? r.data.items : [];
      setGenerations(items);
      const map = {};
      items.forEach((g) => { if (g.lot_id) map[g.lot_id] = 'completed'; });
      setGenByLot(map);
    } catch {
      setGenerations([]);
    } finally {
      setLoadingGenerations(false);
    }
  }, [projectId]);
  // Fetch lot generation status for status pills
  useEffect(() => { fetchGenerations(); }, [fetchGenerations]);
  // Persist section + tab in URL (shallow)
  useEffect(() => {
    const url = new URL(window.location.href);
    if (activeSection !== 'dashboard') url.searchParams.set('section', activeSection); else url.searchParams.delete('section');
    if (activeTab) url.searchParams.set('tab', activeTab); else url.searchParams.delete('tab');
    window.history.replaceState({}, '', url.toString());
  }, [activeSection, activeTab]);
  // Detect pending materialtheque for badge
  useEffect(() => {
    if (!projectId) return;
    axios.get(`${API}/materialtheque/projects/${projectId}/pending`, { withCredentials: true })
      .then(r => setPendingForBadge(!!r.data?.has_pending))
      .catch(() => {});
  }, [projectId]);
  
  // Dialogs
  const [showBuildingDialog, setShowBuildingDialog] = useState(false);
  const [showFloorDialog, setShowFloorDialog] = useState(false);
  const [showLotDialog, setShowLotDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  // Apartment / promotion vocabulary
  const vocab = getProjectVocab(project?.project_type_code);
  
  // Form data
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [selectedLot, setSelectedLot] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [buildingForm, setBuildingForm] = useState({ name: '', address: '', floors_count: 1 });
  const [floorForm, setFloorForm] = useState({ name: '', level: 0, kind: 'courant' });
  const [lotForm, setLotForm] = useState({ 
    name: '', 
    lot_number: '', 
    typology: 'T2', 
    surface: '', 
    price: '',
    style_id: '',
    rooms_config: [],
    correspondance: 'Unique',
  });
  const [validationForm, setValidationForm] = useState({ status: '', comment: '' });

  useEffect(() => {
    fetchAll();
  }, [projectId]);

  const fetchAll = async () => {
    try {
      const [projectRes, buildingsRes, filesRes, stylesRes, materialsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, { withCredentials: true }),
        axios.get(`${API}/projects/${projectId}/buildings`, { withCredentials: true }),
        axios.get(`${API}/projects/${projectId}/files`, { withCredentials: true }),
        axios.get(`${API}/user/styles`, { withCredentials: true }),
        axios.get(`${API}/user/materials`, { withCredentials: true })
      ]);
      
      setProject(projectRes.data);
      setBuildings(buildingsRes.data);
      setFiles(filesRes.data);
      setStyles(stylesRes.data);
      setMaterials(materialsRes.data);
      
      // Fetch floors and lots for each building
      const floorsData = {};
      const lotsData = {};
      
      for (const building of buildingsRes.data) {
        const floorsRes = await axios.get(`${API}/buildings/${building.building_id}/floors`, { withCredentials: true });
        floorsData[building.building_id] = floorsRes.data;
        
        for (const floor of floorsRes.data) {
          const lotsRes = await axios.get(`${API}/floors/${floor.floor_id}/lots`, { withCredentials: true });
          lotsData[floor.floor_id] = lotsRes.data;
        }
      }
      
      setFloors(floorsData);
      setLots(lotsData);
      // Flatten all lots for dropdowns
      const flat = Object.values(lotsData).flat();
      setAllLots(flat);
    } catch (error) {
      toast.error('Erreur lors du chargement');
      // V14.68.15 — retour cohérent vers la liste des projets (non plus /dashboard)
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    
    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await axios.post(
          `${API}/projects/${projectId}/files${selectedLot ? `?lot_id=${selectedLot.lot_id}` : ''}`,
          formData,
          { 
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
        setFiles(prev => [...prev, response.data]);
        toast.success(`${file.name} uploadé avec succès`);
      } catch (error) {
        toast.error(`Erreur lors de l'upload de ${file.name}`);
      }
    }
    
    setUploading(false);
  }, [projectId, selectedLot]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'application/pdf': ['.pdf']
    }
  });

  // Building CRUD
  const handleCreateBuilding = async () => {
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/buildings`,
        buildingForm,
        { withCredentials: true }
      );
      setBuildings([...buildings, response.data]);
      setFloors({ ...floors, [response.data.building_id]: [] });
      setShowBuildingDialog(false);
      setBuildingForm({ name: '', address: '', floors_count: 1 });
      toast.success('Bâtiment créé');
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDeleteBuilding = async (buildingId) => {
    try {
      await axios.delete(`${API}/buildings/${buildingId}`, { withCredentials: true });
      setBuildings(buildings.filter(b => b.building_id !== buildingId));
      toast.success('Bâtiment supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Floor CRUD
  const handleCreateFloor = async () => {
    if (!selectedBuilding) return;
    try {
      const response = await axios.post(
        `${API}/buildings/${selectedBuilding.building_id}/floors`,
        floorForm,
        { withCredentials: true }
      );
      setFloors({
        ...floors,
        [selectedBuilding.building_id]: [...(floors[selectedBuilding.building_id] || []), response.data]
      });
      setLots({ ...lots, [response.data.floor_id]: [] });
      setShowFloorDialog(false);
      setFloorForm({ name: '', level: 0, kind: 'courant' });
      toast.success('Étage créé');
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  // Lot CRUD
  const handleCreateLot = async () => {
    if (!selectedFloor) return;
    try {
      const response = await axios.post(
        `${API}/floors/${selectedFloor.floor_id}/lots`,
        {
          ...lotForm,
          surface: lotForm.surface ? parseFloat(lotForm.surface) : null,
          price: lotForm.price ? parseFloat(lotForm.price) : null
        },
        { withCredentials: true }
      );
      setLots({
        ...lots,
        [selectedFloor.floor_id]: [...(lots[selectedFloor.floor_id] || []), response.data]
      });
      setShowLotDialog(false);
      setLotForm({ name: '', lot_number: '', typology: 'T2', surface: '', price: '', style_id: '', rooms_config: [] });
      toast.success('Lot créé');
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateLot = async () => {
    if (!selectedLot) return;
    try {
      await axios.put(
        `${API}/lots/${selectedLot.lot_id}`,
        {
          ...lotForm,
          surface: lotForm.surface ? parseFloat(lotForm.surface) : null,
          price: lotForm.price ? parseFloat(lotForm.price) : null
        },
        { withCredentials: true }
      );
      // Refresh lots
      const lotsRes = await axios.get(`${API}/floors/${selectedLot.floor_id}/lots`, { withCredentials: true });
      setLots({ ...lots, [selectedLot.floor_id]: lotsRes.data });
      setShowConfigDialog(false);
      toast.success('Lot mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // File validation
  const handleValidateFile = async () => {
    if (!selectedFile) return;
    try {
      await axios.post(
        `${API}/files/${selectedFile.file_id}/validate`,
        validationForm,
        { withCredentials: true }
      );
      // Refresh files
      const filesRes = await axios.get(`${API}/projects/${projectId}/files`, { withCredentials: true });
      setFiles(filesRes.data);
      setShowValidationDialog(false);
      setValidationForm({ status: '', comment: '' });
      toast.success('Validation enregistrée');
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/template`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_projet_${projectId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleExportRapport = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/export-pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Rapport_${project?.name?.replace(/\s/g, '_') || 'projet'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exporté');
    } catch { toast.error('Erreur export PDF'); }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/import`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(response.data.message);
      if (response.data?.warnings?.length) {
        response.data.warnings.slice(0, 3).forEach((w) => toast.warning(w, { duration: 8000 }));
      }
      fetchAll(); // Refresh all data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    }
  };

  const getValidationIcon = (status) => {
    switch (status) {
      case 'approuve': return <Check className="w-4 h-4 text-green-600" />;
      case 'rejete': return <X className="w-4 h-4 text-red-600" />;
      case 'revision_demandee': return <AlertCircle className="w-4 h-4 text-amber-600" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getValidationLabel = (status) => {
    const labels = {
      en_attente: 'En attente',
      revision_demandee: 'Révision demandée',
      approuve: 'Approuvé',
      rejete: 'Rejeté'
    };
    return labels[status] || 'En attente';
  };

  const getValidationColor = (status) => {
    const colors = {
      en_attente: 'bg-gray-100 text-gray-600',
      revision_demandee: 'bg-amber-100 text-amber-700',
      approuve: 'bg-green-100 text-green-700',
      rejete: 'bg-red-100 text-red-700'
    };
    return colors[status] || colors.en_attente;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  return (
    <div data-hud-page data-testid="project-detail-page" className="fade-in">
      {/* V14.68.22 — Layout compact : breadcrumb + menu horizontal + contenu.
          Suppression du gros bloc titre "Maison Test / Aucune description" et
          des boutons top-right (Rapport / Plan de Vente) — désormais dans les
          dropdowns du menu horizontal (Production ▾ / Dossier projet ▾). */}
      <HUDPageHeader
        module="Cockpit"
        breadcrumb={[
          { label: 'Hub', to: '/studio/dashboard' },
          { label: 'Projets', to: '/projects' },
          { label: project?.name || 'Projet' },
        ]}
        icon={FolderOpen}
        title={null}
        subtitle={null}
        compact
      />

      <ProjectHorizontalNav
        activeSection={activeSection}
        activeTab={activeTab}
        onChange={(s, t) => openSection(s, t)}
        projectName={project?.name}
        projectDescription={project?.description}
      />
      <input id="import-file-input" type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportFile} data-testid="import-file-input" />

      <MaterialthequePendingBanner projectId={projectId} />
      <GeolocationBanner
        project={project}
        onGeocoded={(updated) => setProject((p) => ({ ...p, ...updated }))}
      />

      <div className="flex flex-1">
        <div
          key={`${activeSection}:${activeTab || ''}`}
          className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-4 hud-fade-in"
          data-testid={`project-section-${activeSection}`}
        >
          {activeSection === 'dashboard' && (
            <ProjectDashboardSection
              project={project}
              projectId={projectId}
              onOpen={openSection}
              onNavigateToCalendar={(focusKey) => {
                const url = new URL(window.location.href);
                if (focusKey) url.searchParams.set('focus', focusKey); else url.searchParams.delete('focus');
                window.history.replaceState({}, '', url.toString());
                openSection('settings', 'identite');
              }}
            />
          )}

          {activeSection === 'production' && (
            <ProductionSection
              projectId={projectId}
              project={project}
              tab={activeTab}
              hasPending={pendingForBadge}
              focus={focus}
              onOpen={openSection}
              onPlanVente={() => setShowSalesBrochure(true)}
              onRapport={handleExportRapport}
            />
          )}

          {activeSection === 'ressources' && (
            <ProjectResourcesSection
              tab={activeTab}
              projectId={projectId}
              navigate={navigate}
              pendingForBadge={pendingForBadge}
              styles={styles}
              materials={materials}
              filesProps={{
                files, setFiles, buildings, floorsByBuilding: floors, allLots, generations, loadingGenerations,
                onOpenStudio: () => openSection('production', 'generation'),
                onRefresh: async () => { try { const r = await axios.get(`${API}/projects/${projectId}/files`, { withCredentials: true }); setFiles(r.data); } catch { /* ignore */ } fetchGenerations(); },
              }}
              setupProps={{
                projectId, project, navigate, pendingForBadge, engTab, setEngTab, buildings, floors, setFloors, lots, allLots, files, setFiles,
                generations, loadingGenerations, genByLot, engBuildingFilter, setEngBuildingFilter, expandedLotsByFloor, setExpandedLotsByFloor,
                collapsedBuildings, toggleBuildingCollapse, collapsedFloors, toggleFloorCollapse, expandedLotRow, setExpandedLotRow,
                setShowBuildingDialog, setSelectedBuilding, setShowFloorDialog, setSelectedFloor, setShowLotDialog, setSelectedLot, setLotForm, setShowConfigDialog,
                handleDeleteBuilding, fetchGenerations, setActiveSection, setAllLots, setLots, ProjectFilesWorkspace, styles, materials,
                onProjectSaved: (updated) => setProject((p) => ({ ...p, ...updated })),
              }}
            />
          )}

          {activeSection === 'settings' && (
            <ProjectSettingsHub
              tab={activeTab}
              project={project}
              projectId={projectId}
              onSaved={(updated) => setProject((p) => ({ ...p, ...updated }))}
              onOpen={openSection}
            />
          )}
        </div>
      </div>

      {/* Building Dialog */}
      <Dialog open={showBuildingDialog} onOpenChange={setShowBuildingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvel {vocab.building.toLowerCase()}</DialogTitle>
            <DialogDescription>Renseignez le nom et l&apos;adresse.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Nom *</label>
              <Input
                value={buildingForm.name}
                onChange={(e) => setBuildingForm({ ...buildingForm, name: e.target.value })}
                placeholder={`Ex: ${vocab.building} A`}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Adresse</label>
              <Input
                value={buildingForm.address}
                onChange={(e) => setBuildingForm({ ...buildingForm, address: e.target.value })}
                placeholder="Ex: 12 rue de Paris"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBuildingDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateBuilding} className="bg-blue-600 hover:bg-blue-700">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Floor Dialog */}
      <Dialog open={showFloorDialog} onOpenChange={setShowFloorDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvel {vocab.floor.toLowerCase()}</DialogTitle>
            <DialogDescription>Renseignez le nom et le niveau.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Nom *</label>
              <Input
                value={floorForm.name}
                onChange={(e) => setFloorForm({ ...floorForm, name: e.target.value })}
                placeholder="Ex: RDC, 1er étage"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Niveau</label>
              <Input
                type="number"
                value={floorForm.level}
                onChange={(e) => { const lv = parseInt(e.target.value) || 0; setFloorForm({ ...floorForm, level: lv, kind: lv < 0 ? 'sous_sol' : (floorForm.kind === 'sous_sol' ? 'courant' : floorForm.kind) }); }}
                placeholder="0 = RDC, -1 = sous-sol"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Type d'étage</label>
              <div className="flex gap-1.5" data-testid="floor-kind-choice">
                {[['sous_sol', 'Sous-sol', 'parkings, caves — enterré'], ['courant', 'Courant', 'étage habité standard'], ['toit', 'Toit', 'combles, attique, toit-terrasse']].map(([k, l, d]) => (
                  <button key={k} type="button" onClick={() => setFloorForm({ ...floorForm, kind: k, level: k === 'sous_sol' && floorForm.level >= 0 ? -1 : (k !== 'sous_sol' && floorForm.level < 0 ? 0 : floorForm.level) })} title={d}
                    className={`flex-1 h-9 rounded-md border text-[12px] ${floorForm.kind === k ? 'bg-[var(--c-text)] text-[var(--c-surface-0)] border-transparent' : 'border-[var(--c-border)] text-[var(--c-text-2)] hover:bg-[var(--c-surface-2)]'}`} data-testid={`floor-kind-${k}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFloorDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateFloor} className="bg-blue-600 hover:bg-blue-700">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lot Dialog */}
      <Dialog open={showLotDialog} onOpenChange={setShowLotDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{vocab.addLot}</DialogTitle>
            <DialogDescription>Créez un nouveau lot pour cet {vocab.floor.toLowerCase()}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Numéro de lot *</label>
                <Input
                  value={lotForm.lot_number}
                  onChange={(e) => setLotForm({ ...lotForm, lot_number: e.target.value })}
                  placeholder="Ex: A001"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Nom</label>
                <Input
                  value={lotForm.name}
                  onChange={(e) => setLotForm({ ...lotForm, name: e.target.value })}
                  placeholder="Ex: Appartement 001"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Typologie</label>
                <Select value={lotForm.typology} onValueChange={(v) => setLotForm({ ...lotForm, typology: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPOLOGIES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Surface (m²)</label>
                <Input
                  type="number"
                  value={lotForm.surface}
                  onChange={(e) => setLotForm({ ...lotForm, surface: e.target.value })}
                  placeholder="Ex: 65"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Prix indicatif (€)</label>
                <Input
                  type="number"
                  value={lotForm.price}
                  onChange={(e) => setLotForm({ ...lotForm, price: e.target.value })}
                  placeholder="Ex: 285000"
                  data-testid="lot-price-input"
                />
              </div>
            </div>
            <LotCorrespondanceField
              lotForm={lotForm}
              setLotForm={setLotForm}
              allLots={allLots}
              currentLotNumber={lotForm.lot_number}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLotDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateLot} className="bg-blue-600 hover:bg-blue-700">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Dialog (Fiche Lot complète) */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Home className="w-5 h-5 text-blue-600" />
              Fiche Lot {selectedLot?.lot_number || selectedLot?.name}
            </DialogTitle>
            <DialogDescription>Configurez ce lot, ses pièces et ses exports.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">

            {/* === V14.0 — Bandeau d'héritage Unique ↔ Similaire === */}
            <LotInheritanceBanner
              lotId={selectedLot?.lot_id}
              lotForm={lotForm}
              setLotForm={setLotForm}
              allLots={allLots}
              onLotUpdated={async () => {
                if (!selectedLot?.lot_id) return;
                try {
                  const r = await axios.get(`${API}/lots/${selectedLot.lot_id}`, { withCredentials: true });
                  setSelectedLot(r.data);
                  setLotForm((prev) => ({ ...(prev || {}), correspondance: r.data.correspondance, master_lot_id: r.data.master_lot_id }));
                  if (selectedLot.floor_id) {
                    const lots_r = await axios.get(`${API}/floors/${selectedLot.floor_id}/lots`, { withCredentials: true });
                    setLots(prev => ({ ...prev, [selectedLot.floor_id]: lots_r.data }));
                  }
                } catch (e) { /* silent */ }
              }}
            />

            {/* === WORKFLOW PIPELINE === */}
            <div data-testid="workflow-pipeline">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Suivi de production</h4>
              <div className="flex items-center gap-1">
                {[
                  { key: 'analyse_pre_projet', label: 'Analyse pré-projet', color: 'bg-slate-500' },
                  { key: 'analyse_documents', label: 'Analyse documents', color: 'bg-indigo-500' },
                  { key: 'demande_validee', label: 'Demande en cours', color: 'bg-blue-500' },
                  { key: 'livraison_beta', label: 'Livraison bêta', color: 'bg-amber-500' },
                  { key: 'validation_client', label: 'Validation client', color: 'bg-orange-500' },
                  { key: 'livraison_finale', label: 'Livraison finale', color: 'bg-green-600' }
                ].map((step, i, arr) => {
                  const currentIdx = arr.findIndex(s => s.key === (selectedLot?.workflow_status || 'analyse_pre_projet'));
                  const isActive = i <= currentIdx;
                  const isCurrent = i === currentIdx;
                  return (
                    <button
                      key={step.key}
                      className={`flex-1 py-1.5 text-[10px] font-medium rounded transition-colors text-center ${
                        isActive ? `${step.color} text-white` : 'bg-gray-100 text-gray-400'
                      } ${isCurrent ? 'ring-2 ring-offset-1 ring-blue-400' : ''}`}
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/lots/${selectedLot.lot_id}`, 
                            { workflow_status: step.key },
                            { withCredentials: true }
                          );
                          setSelectedLot({ ...selectedLot, workflow_status: step.key });
                          // Refresh lots
                          if (selectedLot.floor_id) {
                            const r = await axios.get(`${API}/floors/${selectedLot.floor_id}/lots`, { withCredentials: true });
                            setLots(prev => ({ ...prev, [selectedLot.floor_id]: r.data }));
                          }
                          toast.success(`Statut: ${step.label}`);
                        } catch { toast.error('Erreur mise à jour statut'); }
                      }}
                      data-testid={`workflow-step-${step.key}`}
                    >
                      {step.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* === PLAN FILE SELECTION === */}
            <div className="border-t border-gray-100 pt-4" data-testid="plan-file-section">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Plan retenu pour génération</h4>
              {files.filter(f => f.content_type?.startsWith('image/')).length > 0 ? (
                <div className="space-y-2">
                  <Select
                    value={selectedLot?.selected_plan_file_id || 'none'}
                    onValueChange={async (fileId) => {
                      try {
                        await axios.put(`${API}/lots/${selectedLot.lot_id}`,
                          { selected_plan_file_id: fileId === 'none' ? '' : fileId },
                          { withCredentials: true }
                        );
                        setSelectedLot({ ...selectedLot, selected_plan_file_id: fileId === 'none' ? null : fileId });
                        setAllLots(prev => prev.map(l => l.lot_id === selectedLot.lot_id ? { ...l, selected_plan_file_id: fileId === 'none' ? null : fileId } : l));
                        toast.success(fileId !== 'none' ? 'Plan sélectionné' : 'Plan détaché');
                      } catch { toast.error('Erreur sélection'); }
                    }}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid="lot-plan-select">
                      <SelectValue placeholder="Choisir un plan..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun plan</SelectItem>
                      {files.filter(f => f.content_type?.startsWith('image/')).map(f => (
                        <SelectItem key={f.file_id} value={f.file_id}>
                          {f.original_filename || f.original_name || f.file_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Preview thumbnail */}
                  {selectedLot?.selected_plan_file_id && (
                    <div className="w-full h-32 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                      <img
                        src={`${API}/files/${selectedLot.selected_plan_file_id}/download`}
                        alt="Plan sélectionné"
                        className="w-full h-full object-contain"
                        crossOrigin="use-credentials"
                        onError={(e) => { e.target.style.display='none'; }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Aucun fichier image. Uploadez des plans dans l'onglet Fichiers.</p>
              )}
            </div>

            {/* === LOT INFO === */}
            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Informations</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Numéro</label>
                  <Input value={lotForm.lot_number} onChange={(e) => setLotForm({ ...lotForm, lot_number: e.target.value })} className="h-8 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Nom</label>
                  <Input value={lotForm.name} onChange={(e) => setLotForm({ ...lotForm, name: e.target.value })} className="h-8 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Typologie</label>
                  <Select value={lotForm.typology} onValueChange={(v) => setLotForm({ ...lotForm, typology: v })}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPOLOGIES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Surface (m²)</label>
                  <Input type="number" value={lotForm.surface} onChange={(e) => setLotForm({ ...lotForm, surface: e.target.value })} className="h-8 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Prix indicatif (€)</label>
                  <Input type="number" value={lotForm.price} onChange={(e) => setLotForm({ ...lotForm, price: e.target.value })} className="h-8 text-sm" data-testid="lot-config-price-input" placeholder="Ex: 285000" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-500 block mb-1">Style</label>
                  <Select value={lotForm.style_id || 'none'} onValueChange={(v) => setLotForm({ ...lotForm, style_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Aucun" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {styles.map(s => <SelectItem key={s.style_id} value={s.style_id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 pt-1">
                  <LotCorrespondanceField
                    lotForm={lotForm}
                    setLotForm={setLotForm}
                    allLots={allLots}
                    currentLotNumber={lotForm.lot_number}
                    compact
                  />
                </div>
              </div>
            </div>

            {/* === ROOMS MATERIALS === */}
            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Matériaux par pièce</h4>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setLotForm({
                  ...lotForm, rooms_config: [...(lotForm.rooms_config || []), { room: '', floor_material: '', wall_material: '' }]
                })} data-testid="add-room-btn">+ Pièce</Button>
              </div>
              {(lotForm.rooms_config || []).map((room, idx) => (
                <div key={idx} className="flex items-center gap-2 mb-1.5" data-testid={`room-config-${idx}`}>
                  <Input value={room.room} onChange={(e) => { const u=[...lotForm.rooms_config]; u[idx]={...u[idx],room:e.target.value}; setLotForm({...lotForm,rooms_config:u}); }}
                    placeholder="Pièce" className="h-7 text-xs flex-1" data-testid={`room-name-${idx}`} />
                  <Select value={room.floor_material||'none'} onValueChange={(v) => { const u=[...lotForm.rooms_config]; u[idx]={...u[idx],floor_material:v==='none'?'':v}; setLotForm({...lotForm,rooms_config:u}); }}>
                    <SelectTrigger className="h-7 text-xs w-32" data-testid={`room-floor-${idx}`}><SelectValue placeholder="Sol" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">-</SelectItem>{materials.filter(m=>m.category==='sol').map(m=><SelectItem key={m.material_id} value={m.material_id}>{m.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={room.wall_material||'none'} onValueChange={(v) => { const u=[...lotForm.rooms_config]; u[idx]={...u[idx],wall_material:v==='none'?'':v}; setLotForm({...lotForm,rooms_config:u}); }}>
                    <SelectTrigger className="h-7 text-xs w-32" data-testid={`room-wall-${idx}`}><SelectValue placeholder="Mur" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">-</SelectItem>{materials.filter(m=>m.category==='mur').map(m=><SelectItem key={m.material_id} value={m.material_id}>{m.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400 hover:bg-red-50 shrink-0" onClick={() => {
                    setLotForm({...lotForm, rooms_config: lotForm.rooms_config.filter((_,i)=>i!==idx)});
                  }}><span className="text-xs">×</span></Button>
                </div>
              ))}
            </div>

            {/* === WORKFLOW NOTES / RETAKES === */}
            <div className="border-t border-gray-100 pt-4" data-testid="workflow-notes">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Notes & Retakes</h4>
              <div className="space-y-1.5 max-h-32 overflow-y-auto mb-2">
                {(selectedLot?.workflow_notes || []).length === 0 ? (
                  <p className="text-xs text-gray-400">Aucune note</p>
                ) : (
                  (selectedLot?.workflow_notes || []).slice().reverse().map((n, i) => (
                    <div key={i} className={`text-xs p-2 rounded ${
                      n.type === 'retake' ? 'bg-amber-50 border border-amber-200' :
                      n.type === 'validation' ? 'bg-green-50 border border-green-200' :
                      'bg-gray-50 border border-gray-200'
                    }`}>
                      <div className="flex justify-between">
                        <span className="font-medium">{n.author || ''}</span>
                        <span className="text-gray-400">{n.date?.slice(0,10)}</span>
                      </div>
                      <p className="mt-0.5">{n.note}</p>
                      {n.type === 'retake' && <span className="text-amber-600 font-medium">Retake</span>}
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  id="workflow-note-input"
                  placeholder="Ajouter une note ou retake..."
                  className="h-8 text-xs flex-1"
                  data-testid="workflow-note-input"
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      try {
                        const res = await axios.post(`${API}/lots/${selectedLot.lot_id}/workflow-note`,
                          { note: e.target.value.trim(), type: 'comment' },
                          { withCredentials: true }
                        );
                        setSelectedLot(res.data);
                        e.target.value = '';
                      } catch { toast.error('Erreur'); }
                    }
                  }}
                />
                <Button size="sm" variant="outline" className="h-8 text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                  onClick={async () => {
                    const input = document.getElementById('workflow-note-input');
                    if (!input?.value?.trim()) return;
                    try {
                      const res = await axios.post(`${API}/lots/${selectedLot.lot_id}/workflow-note`,
                        { note: input.value.trim(), type: 'retake' },
                        { withCredentials: true }
                      );
                      setSelectedLot(res.data);
                      input.value = '';
                      toast.success('Retake ajouté');
                    } catch { toast.error('Erreur'); }
                  }}
                  data-testid="add-retake-btn"
                >Retake</Button>
              </div>
            </div>

            {/* === EXPORTS === */}
            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Exports</h4>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => {
                  setShowConfigDialog(false);
                  setShowSalesBrochure(true);
                }} data-testid="lot-export-sales-btn">
                  <FileDown className="w-3.5 h-3.5 mr-1.5" />Plan de Vente
                </Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/projects/${projectId}/export-pdf`, { withCredentials: true, responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    const a = document.createElement('a'); a.href = url; a.download = `Rapport_${project?.name?.replace(/\s/g,'_')}.pdf`; a.click();
                    toast.success('Rapport exporté');
                  } catch { toast.error('Erreur export'); }
                }} data-testid="lot-export-report-btn">
                  <FileDown className="w-3.5 h-3.5 mr-1.5" />Rapport projet
                </Button>
              </div>
            </div>

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Fermer</Button>
            <Button onClick={handleUpdateLot} className="bg-blue-600 hover:bg-blue-700">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validation Dialog */}
      <Dialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Valider le fichier</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">Fichier: {selectedFile?.original_filename}</p>
            
            {/* Validation History */}
            {selectedFile?.validation_history?.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Historique</p>
                <div className="space-y-2">
                  {selectedFile.validation_history.map((entry, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      {getValidationIcon(entry.status)}
                      <div>
                        <span className="font-medium">{getValidationLabel(entry.status)}</span>
                        <span className="text-gray-400 ml-2">{formatDate(entry.timestamp)}</span>
                        {entry.comment && <p className="text-gray-500">{entry.comment}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Nouveau statut</label>
              <Select value={validationForm.status} onValueChange={(v) => setValidationForm({ ...validationForm, status: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="revision_demandee">Révision demandée</SelectItem>
                  <SelectItem value="approuve">Approuvé</SelectItem>
                  <SelectItem value="rejete">Rejeté</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Commentaire</label>
              <Input
                value={validationForm.comment}
                onChange={(e) => setValidationForm({ ...validationForm, comment: e.target.value })}
                placeholder="Optionnel..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValidationDialog(false)}>Annuler</Button>
            <Button 
              onClick={handleValidateFile} 
              disabled={!validationForm.status}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Valider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sales Brochure Config Dialog */}
      {showSalesBrochure && (
        <SalesBrochureDialog
          projectId={projectId}
          projectName={project?.name}
          onClose={() => setShowSalesBrochure(false)}
        />
      )}
    </div>
  );
};

export default ProjectDetailPage;
